import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

// shadcn UI components
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../components/ui/use-toast';

// icons
import { Eye, EyeSlash, Lock } from 'iconsax-react';

// project imports
import { resetPassword } from '../../services/authApi';

// validation schema (cleaned up useless escapes to fix ESLint warnings)
const validationSchema = Yup.object({
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters long')
    .matches(/[a-z]/, 'Password must contain at least one lowercase alphabet')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase alphabet')
    .matches(/[0-9]/, 'Password must contain at least one number')
    .matches(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/, 'Password must contain at least one special character')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords do not match')
    .required('Confirm password is required')
});

// ==============================|| RESET PASSWORD PAGE ||============================== //

const ResetPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const storedEmail = sessionStorage.getItem('forgot_email');
    if (!storedEmail) {
      toast({
        title: "Session Expired",
        description: "No email found in session. Redirecting to forgot password...",
        variant: "destructive"
      });
      const timer = setTimeout(() => {
        navigate('/forgot-password');
      }, 2000);
      return () => clearTimeout(timer);
    }
    setEmail(storedEmail);
  }, [navigate, toast]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const result = await resetPassword(email, values.password, values.confirmPassword);
      if (result.success) {
        toast({
          title: "Password Reset Successful",
          description: "Your password has been reset successfully! Redirecting to login...",
        });
        
        // Clear session storage
        sessionStorage.removeItem('forgot_email');

        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        toast({
          title: "Reset Failed",
          description: result.message || 'Failed to reset password. Please try again.',
          variant: "destructive"
        });
      }
    } catch (err) {
      console.error('Reset Password error:', err);
      toast({
        title: "Reset Failed",
        description: err.response?.data?.message || 'Failed to reset password. Please try again.',
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-background p-4 sm:p-6">
      <Card className="w-full max-w-[400px] border-border shadow-lg">
        <CardContent className="p-8">
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-3xl font-extrabold tracking-tight text-foreground">
              Reset Password
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              Create a new strong password for your account.
            </p>
          </div>

          <Formik
            initialValues={{ password: '', confirmPassword: '' }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
              <Form className="space-y-4">
                {/* Password field */}
                <div className="space-y-1.5">
                  <Label htmlFor="password">New Password</Label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground">
                      <Lock size={20} />
                    </div>
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`pl-10 pr-10 ${
                        touched.password && errors.password ? 'border-destructive focus-visible:ring-destructive' : ''
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground focus:outline-none transition-colors"
                    >
                      {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {touched.password && errors.password && (
                    <p className="text-xs text-destructive">{errors.password}</p>
                  )}
                </div>

                {/* Confirm Password field */}
                <div className="space-y-1.5">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground">
                      <Lock size={20} />
                    </div>
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={values.confirmPassword}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`pl-10 pr-10 ${
                        touched.confirmPassword && errors.confirmPassword ? 'border-destructive focus-visible:ring-destructive' : ''
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground focus:outline-none transition-colors"
                    >
                      {showConfirmPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {touched.confirmPassword && errors.confirmPassword && (
                    <p className="text-xs text-destructive">{errors.confirmPassword}</p>
                  )}
                </div>

                {/* Submit button */}
                <Button
                  type="submit"
                  disabled={isSubmitting || !email}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-11 shadow-md transition-all duration-200 mt-2 flex items-center justify-center gap-2"
                >
                  {isSubmitting && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
                </Button>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
